import {
  AdsbSensorConfiguration,
  AeroscopeSensorConfiguration,
  ArdronisAntenna,
  ArdronisSensorConfiguration,
  AutomatonCabinet,
  AutomatonSegment,
  AutomatonSensorConfiguration,
  AvisoSensorConfiguration,
  BlastSensorConfiguration,
  Dfb6SensorConfiguration,
  DroneShieldSensorConfiguration,
  FlirSensorConfiguration,
  GenericSensorConfiguration,
  GenericSubSensor,
  GroundStationSensorConfiguration,
  HolodroneSensorConfiguration,
  HolopticsSensorConfiguration,
  MajesSensorConfiguration,
  OnvifSensorConfiguration,
  RobinMaxRadar,
  RobinMaxSensorConfiguration,
  SenidAntenna,
  SenidSensorConfiguration,
  SkyEyeAntenna,
  SkyEyeSensorConfiguration,
  SkyJackerAntenna,
  SkyJackerSensorConfiguration,
  SpexerRadar,
  SpexerSensorConfiguration,
  XentaSensorConfiguration,
  ZmerSensorConfiguration,
} from './sensors';
import { SensorTypeEnum, SubSensorTypeEnum } from './sensors/common.types';

export const CAMERA_SENSORS = [
  SensorTypeEnum.AF2206,
  SensorTypeEnum.AXIS,
  SensorTypeEnum.ONVIF,
  SensorTypeEnum.ZMER,
] as const satisfies readonly SensorTypeEnum[];
export type CameraType = (typeof CAMERA_SENSORS)[number];

export const NEUTRAL_SENSORS = [
  SensorTypeEnum.SKY_JACKER,
  SensorTypeEnum.MAJES,
  SensorTypeEnum.BLAST,
  SensorTypeEnum.DFB6,
] as const satisfies readonly SensorTypeEnum[];
export type NeutralType = (typeof NEUTRAL_SENSORS)[number];

// All extended neutral sensors will appear red on the map during emission
export const EXTENDED_NEUTRAL_SENSORS = [
  ...NEUTRAL_SENSORS,
  SubSensorTypeEnum.SKY_JACKER_ANTENNA,
] as const satisfies readonly (SensorTypeEnum | SubSensorTypeEnum)[];

export const NEUTRAL_SENSORS_WITH_SECTORS = [
  SensorTypeEnum.MAJES,
  SensorTypeEnum.BLAST,
  SensorTypeEnum.DFB6,
] as const satisfies readonly SensorTypeEnum[];
export type NeutralSensorWithSectorsType = (typeof NEUTRAL_SENSORS_WITH_SECTORS)[number];

export const SUB_SENSORS = [
  SubSensorTypeEnum.ARDRONIS_ANTENNA,
  SubSensorTypeEnum.AUTOMATON_CABINET,
  SubSensorTypeEnum.AUTOMATON_SEGMENT,
  SubSensorTypeEnum.GENERIC_SUB_SENSOR,
  SubSensorTypeEnum.ROBIN_MAX_RADAR,
  SubSensorTypeEnum.SENID_ANTENNA,
  SubSensorTypeEnum.SKY_EYE_ANTENNA,
  SubSensorTypeEnum.SKY_JACKER_ANTENNA,
  SubSensorTypeEnum.SPEXER_RADAR,
] as const satisfies readonly SubSensorTypeEnum[];
export type SubSensorType = (typeof SUB_SENSORS)[number];

export const SENSORS_WITH_SUBSENSOR = [
  SensorTypeEnum.ARDRONIS,
  SensorTypeEnum.AUTOMATON,
  SensorTypeEnum.GENERIC,
  SensorTypeEnum.ROBIN_MAX,
  SensorTypeEnum.SENID,
  SensorTypeEnum.SKY_EYE,
  SensorTypeEnum.SKY_JACKER,
  SensorTypeEnum.SPEXER,
] as const satisfies readonly SensorTypeEnum[];
export type SensorWithSubSensorType = (typeof SENSORS_WITH_SUBSENSOR)[number];

export const RADAR_SENSORS = [
  SensorTypeEnum.FLIR,
  SensorTypeEnum.ROBIN_MAX,
  SensorTypeEnum.SPEXER,
  SensorTypeEnum.XENTA,
] as const satisfies readonly SensorTypeEnum[];
export type RadarType = (typeof RADAR_SENSORS)[number];

export const EXTENDED_RADAR_SENSORS = [
  ...RADAR_SENSORS,
  SubSensorTypeEnum.ROBIN_MAX_RADAR,
  SubSensorTypeEnum.SPEXER_RADAR,
] as const satisfies readonly (SensorTypeEnum | SubSensorTypeEnum)[];

export const DETECTION_SENSORS: readonly SensorTypeEnum[] = [
  SensorTypeEnum.ADSB,
  SensorTypeEnum.AEROSCOPE,
  SensorTypeEnum.ARDRONIS,
  SensorTypeEnum.AVISO,
  SensorTypeEnum.DRONE_SHIELD,
  SensorTypeEnum.FLIR,
  SensorTypeEnum.GENERIC,
  SensorTypeEnum.GROUND_STATION,
  SensorTypeEnum.HOLODRONE,
  SensorTypeEnum.ROBIN_MAX,
  SensorTypeEnum.SENID,
  SensorTypeEnum.SKY_EYE,
  SensorTypeEnum.SKY_JACKER,
  SensorTypeEnum.SPEXER,
  SensorTypeEnum.XENTA,
];

export type SensorConfigurationByType<SensorType extends SensorTypeEnum> = SensorType extends SensorTypeEnum.ADSB
  ? AdsbSensorConfiguration
  : SensorType extends SensorTypeEnum.AEROSCOPE
    ? AeroscopeSensorConfiguration
    : SensorType extends SensorTypeEnum.ARDRONIS
      ? ArdronisSensorConfiguration
      : SensorType extends SensorTypeEnum.AUTOMATON
        ? AutomatonSensorConfiguration
        : SensorType extends SensorTypeEnum.AVISO
          ? AvisoSensorConfiguration
          : SensorType extends SensorTypeEnum.BLAST
            ? BlastSensorConfiguration
            : SensorType extends SensorTypeEnum.DFB6
              ? Dfb6SensorConfiguration
              : SensorType extends SensorTypeEnum.DRONE_SHIELD
                ? DroneShieldSensorConfiguration
                : SensorType extends SensorTypeEnum.FLIR
                  ? FlirSensorConfiguration
                  : SensorType extends SensorTypeEnum.GENERIC
                    ? GenericSensorConfiguration
                    : SensorType extends SensorTypeEnum.GROUND_STATION
                      ? GroundStationSensorConfiguration
                      : SensorType extends SensorTypeEnum.HOLODRONE
                        ? HolodroneSensorConfiguration
                        : SensorType extends SensorTypeEnum.MAJES
                          ? MajesSensorConfiguration
                          : SensorType extends SensorTypeEnum.AF2206
                            ? HolopticsSensorConfiguration
                            : SensorType extends SensorTypeEnum.AXIS
                              ? HolopticsSensorConfiguration
                              : SensorType extends SensorTypeEnum.ROBIN_MAX
                                ? RobinMaxSensorConfiguration
                                : SensorType extends SensorTypeEnum.ONVIF
                                  ? OnvifSensorConfiguration
                                  : SensorType extends SensorTypeEnum.SENID
                                    ? SenidSensorConfiguration
                                    : SensorType extends SensorTypeEnum.SKY_EYE
                                      ? SkyEyeSensorConfiguration
                                      : SensorType extends SensorTypeEnum.SKY_JACKER
                                        ? SkyJackerSensorConfiguration
                                        : SensorType extends SensorTypeEnum.SPEXER
                                          ? SpexerSensorConfiguration
                                          : SensorType extends SensorTypeEnum.XENTA
                                            ? XentaSensorConfiguration
                                            : SensorType extends SensorTypeEnum.ZMER
                                              ? ZmerSensorConfiguration
                                              : never;

export type SensorConfiguration = SensorConfigurationByType<SensorTypeEnum>;

export type SubSensorConfigurationWithParentType<SensorType extends SensorWithSubSensorType> =
  SensorType extends SensorTypeEnum.ARDRONIS
    ? SensorConfigurationByType<SensorTypeEnum.ARDRONIS>['antennas'][number]
    : SensorType extends SensorTypeEnum.AUTOMATON
      ? SensorConfigurationByType<SensorTypeEnum.AUTOMATON>['cabinets'][number]
      : SensorType extends SensorTypeEnum.GENERIC
        ? SensorConfigurationByType<SensorTypeEnum.GENERIC>['subSensors'][number]
        : SensorType extends SensorTypeEnum.ROBIN_MAX
          ? SensorConfigurationByType<SensorTypeEnum.ROBIN_MAX>['radars'][number]
          : SensorType extends SensorTypeEnum.SENID
            ? SensorConfigurationByType<SensorTypeEnum.SENID>['antennas'][number]
            : SensorType extends SensorTypeEnum.SKY_EYE
              ? SensorConfigurationByType<SensorTypeEnum.SKY_EYE>['antennas'][number]
              : SensorType extends SensorTypeEnum.SKY_JACKER
                ? SensorConfigurationByType<SensorTypeEnum.SKY_JACKER>['antennas'][number]
                : SensorType extends SensorTypeEnum.SPEXER
                  ? SensorConfigurationByType<SensorTypeEnum.SPEXER>['radars'][number]
                  : never;

export type SubSensorConfigurationByType<SubSensorType extends SubSensorTypeEnum> =
  SubSensorType extends SubSensorTypeEnum.ARDRONIS_ANTENNA
    ? ArdronisAntenna
    : SubSensorType extends SubSensorTypeEnum.AUTOMATON_CABINET
      ? AutomatonCabinet
      : SubSensorType extends SubSensorTypeEnum.AUTOMATON_SEGMENT
        ? AutomatonSegment
        : SubSensorType extends SubSensorTypeEnum.GENERIC_SUB_SENSOR
          ? GenericSubSensor
          : SubSensorType extends SubSensorTypeEnum.ROBIN_MAX_RADAR
            ? RobinMaxRadar
            : SubSensorType extends SubSensorTypeEnum.SENID_ANTENNA
              ? SenidAntenna
              : SubSensorType extends SubSensorTypeEnum.SKY_EYE_ANTENNA
                ? SkyEyeAntenna
                : SubSensorType extends SubSensorTypeEnum.SKY_JACKER_ANTENNA
                  ? SkyJackerAntenna
                  : SubSensorType extends SubSensorTypeEnum.SPEXER_RADAR
                    ? SpexerRadar
                    : never;

export type SensorConfigurationWithSubSensorType<
  SubSensorType extends Exclude<SubSensorTypeEnum, SubSensorTypeEnum.AUTOMATON_SEGMENT>,
> = SubSensorType extends SubSensorTypeEnum.ARDRONIS_ANTENNA
  ? ArdronisSensorConfiguration
  : SubSensorType extends SubSensorTypeEnum.AUTOMATON_CABINET
    ? AutomatonSensorConfiguration
    : SubSensorType extends SubSensorTypeEnum.GENERIC_SUB_SENSOR
      ? GenericSensorConfiguration
      : SubSensorType extends SubSensorTypeEnum.SENID_ANTENNA
        ? SenidSensorConfiguration
        : SubSensorType extends SubSensorTypeEnum.SKY_EYE_ANTENNA
          ? SkyEyeSensorConfiguration
          : SubSensorType extends SubSensorTypeEnum.SKY_JACKER_ANTENNA
            ? SkyJackerSensorConfiguration
            : SubSensorType extends SubSensorTypeEnum.SPEXER_RADAR
              ? SpexerSensorConfiguration
              : never;

export type SubSensorConfiguration = SubSensorConfigurationByType<SubSensorTypeEnum>;

export type SensorOrSubSensorConfigurationByType<SensorType extends SensorTypeEnum | SubSensorTypeEnum> =
  SensorType extends SubSensorTypeEnum
    ? SubSensorConfigurationByType<Extract<SensorType, SubSensorTypeEnum>>
    : SensorConfigurationByType<Extract<SensorType, SensorTypeEnum>>;

export type SensorOrSubSensorConfiguration = SensorOrSubSensorConfigurationByType<SensorTypeEnum | SubSensorTypeEnum>;

export type RadarSensorConfiguration = Extract<SensorConfiguration, { type: RadarType }>;
export type CameraConfiguration = Extract<SensorConfiguration, { type: CameraType }>;
export type NeutralConfigurationWithSectors = Extract<SensorConfiguration, { type: NeutralSensorWithSectorsType }>;

export type RadarConfiguration =
  | SensorOrSubSensorConfigurationByType<SensorTypeEnum.FLIR>
  | SubSensorConfigurationWithParentType<SensorTypeEnum.ROBIN_MAX>
  | SubSensorConfigurationWithParentType<SensorTypeEnum.SPEXER>
  | SensorOrSubSensorConfigurationByType<SensorTypeEnum.XENTA>;
